import { useEffect, useMemo, useState } from 'react'
import { usePage, router, useForm } from '@inertiajs/react'
import { Input as AntInput } from 'antd'
import { Form, message, Space, Button } from '@/components/ui'
import { UserAddOutlined, EyeOutlined } from '@ant-design/icons'
import EventsLayout from '@/components/layouts/EventLayout'
import { PageHeader } from '@/components/patterns'
import FixedBottomActions from '@/components/layouts/events/FixedBottomActions'
import type { PageProps as InertiaPageProps } from '@inertiajs/core'

import {
  RecipientSelectionDrawer,
  type RecipientSelectionDrawerProps,
} from '../components/recipient_selection/RecipientSelectionDrawer'
import { PreviewModal } from './components/PreviewModal'
import { RichTextEditor } from './components/RichTextEditor'
import type {
  Recipient,
  RecipientFilters,
  PushNotificationFormState,
  RecipientFilterOptions,
} from './components/types'
import { defaultRecipientFilters } from '../components/recipient_selection/types'
import '@/styles/compact-form.css'

interface PushNotificationFormValues {
  title: string
  content: string
  recipientIds: string[]
}

type Event = {
  id: number
  name: string
  slug: string
  start_date: string
  end_date: string
}

interface PushNotificationPageProps extends InertiaPageProps {
  event: Event
  availableRecipients: Recipient[]
  initialNotification: PushNotificationFormState
  initialRecipientFilters: RecipientFilters
  filterOptions: RecipientFilterOptions
  formMode: 'create' | 'edit'
  errors?: Record<string, string>
}

const PushNotificationFormPage = () => {
  const {
    event,
    availableRecipients = [],
    initialNotification,
    initialRecipientFilters,
    filterOptions,
    formMode,
  } = usePage<PushNotificationPageProps>().props

  const [form] = Form.useForm<PushNotificationFormValues>()

  const initialValues = useMemo(
    () => ({
      title: initialNotification?.title || '',
      content: initialNotification?.content || '',
      recipientIds: initialNotification?.recipientIds || [],
    }),
    [initialNotification]
  )

  const [titleValue, setTitleValue] = useState(initialValues.title)
  const [contentValue, setContentValue] = useState(initialValues.content)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [recipientFilters, setRecipientFilters] = useState<RecipientFilters>(
    initialRecipientFilters || defaultRecipientFilters
  )
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>(
    initialValues.recipientIds || []
  )

  const inertiaForm = useForm({
    push_notification: {
      title: initialValues.title,
      content: initialValues.content,
      recipient_ids: initialValues.recipientIds,
      recipient_filters: initialRecipientFilters || defaultRecipientFilters,
    },
  })

  useEffect(() => {
    form.setFieldsValue(initialValues)
    inertiaForm.setData('push_notification', {
      title: initialValues.title,
      content: initialValues.content,
      recipient_ids: initialValues.recipientIds,
      recipient_filters: recipientFilters,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    form.setFieldValue('recipientIds', selectedRecipientIds)
    inertiaForm.setData('push_notification', {
      ...(inertiaForm.data.push_notification || {}),
      recipient_ids: selectedRecipientIds,
      recipient_filters: recipientFilters,
    })
  }, [selectedRecipientIds, recipientFilters])

  const handleRecipientApply: RecipientSelectionDrawerProps['onApply'] = ({
    recipientIds,
    filters,
  }) => {
    setSelectedRecipientIds(recipientIds)
    setRecipientFilters(filters)
    inertiaForm.setData('push_notification', {
      ...(inertiaForm.data.push_notification || {}),
      recipient_ids: recipientIds,
      recipient_filters: filters,
    })
  }

  const handleClearRecipients = () => {
    setRecipientFilters(defaultRecipientFilters)
    setSelectedRecipientIds([])
    form.setFieldValue('recipientIds', [])
    inertiaForm.setData('push_notification', {
      ...(inertiaForm.data.push_notification || {}),
      recipient_ids: [],
      recipient_filters: defaultRecipientFilters,
    })
  }

  const handleSubmit = (values: PushNotificationFormValues) => {
    if (!values.title?.trim()) {
      message.error('Title is required')
      return
    }

    if (!values.content?.trim()) {
      message.error('Content is required')
      return
    }

    if (selectedRecipientIds.length === 0) {
      message.error('Select at least one recipient')
      return
    }

    const url =
      formMode === 'edit'
        ? `/events/${event.slug}/messaging/push_notifications/${initialNotification?.id}`
        : `/events/${event.slug}/messaging/push_notifications`

    // keep Inertia form data in sync before submit
    inertiaForm.setData('push_notification', {
      title: values.title,
      content: values.content,
      recipient_ids: selectedRecipientIds,
      recipient_filters: recipientFilters,
    })

    const submit = formMode === 'edit' ? inertiaForm.patch : inertiaForm.post

    submit(url, {
      preserveScroll: true,
      onSuccess: () => {
        message.success(
          formMode === 'edit' ? 'Push notification updated.' : 'Push notification saved as draft.'
        )
      },
      onError: (formErrors) => {
        const firstError = formErrors && Object.values(formErrors)[0]
        const normalizedError = Array.isArray(firstError) ? firstError[0] : firstError
        message.error(normalizedError || 'Failed to save push notification.')
      },
    })
  }

  const handleCancel = () => {
    router.visit(`/events/${event.slug}/messaging/push_notifications`)
  }

  const handlePreview = () => {
    setPreviewOpen(true)
  }

  const recipientCount = selectedRecipientIds.length
  const recipientSummary = useMemo(() => {
    if (recipientFilters.allAttendees) {
      return `All attendees (${recipientCount || availableRecipients.length})`
    }

    if (recipientCount === 0) return 'No recipients selected'

    return `${recipientCount} attendee${recipientCount === 1 ? '' : 's'} selected`
  }, [recipientCount, recipientFilters.allAttendees, availableRecipients.length])

  return (
    <EventsLayout>
      <PageHeader
        title={formMode === 'edit' ? 'Edit Push Notification' : 'Create Push Notification'}
        subtitle="Create and send text-based notifications via the Event Owner Portal."
        breadcrumb={[
          {
            title: 'Push Notifications',
            href: `/events/${event.slug}/messaging/push_notifications`,
          },
          { title: formMode === 'edit' ? 'Edit' : 'Create' },
        ]}
        showBackButton
        onBack={handleCancel}
      />

      <section className="px-6 py-6 pb-24">
        <div
          style={{ maxWidth: '672px' }}
          className="rounded border border-gray-200 bg-white p-6 shadow-sm"
        >
          <Form
            size="small"
            form={form}
            layout="vertical"
            className="compact-form"
            onFinish={handleSubmit}
            initialValues={initialValues}
            onValuesChange={(_, values) => {
              setTitleValue(values.title || '')
              setContentValue(values.content || '')
              inertiaForm.setData('push_notification', {
                ...(inertiaForm.data.push_notification || {}),
                title: values.title || '',
                content: values.content || '',
              })
            }}
          >
            <Form.Item
              label="Push Notification Title"
              name="title"
              rules={[{ required: true, message: 'Title is required' }]}
              className="mb-6"
            >
              <AntInput allowClear maxLength={120} placeholder="Enter push notification title" />
            </Form.Item>

            <Form.Item
              label="Content"
              name="content"
              rules={[{ required: true, message: 'Content is required' }]}
              className="mb-6"
            >
              <RichTextEditor
                value={contentValue}
                onChange={(html) => {
                  setContentValue(html)
                  form.setFieldValue('content', html)
                  inertiaForm.setData('push_notification', {
                    ...(inertiaForm.data.push_notification || {}),
                    content: html,
                  })
                }}
                placeholder="Enter your message (supports bold, italic, bullets)"
                maxLength={1000}
              />
            </Form.Item>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select recipients <span className="text-red-500">*</span>
              </label>
              <div
                className="flex min-h-[46px] items-center justify-between rounded border border-gray-300 bg-white px-3 py-2"
                onClick={() => setDrawerOpen(true)}
              >
                <div className="flex flex-col">
                  <span className="text-sm text-black/85">{recipientSummary}</span>
                  <span className="text-xs text-black/45">
                    {recipientFilters.allAttendees
                      ? 'All current registrants will receive this push.'
                      : 'Choose attendees to include.'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {recipientCount > 0 && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                    >
                      <Button variant="text" onClick={handleClearRecipients}>
                        Clear
                      </Button>
                    </div>
                  )}
                  <Button
                    variant="text"
                    icon={<UserAddOutlined />}
                    onClick={() => setDrawerOpen(true)}
                  >
                    Select
                  </Button>
                </div>
              </div>
            </div>

            {/* Hidden form fields for validation */}
            <Form.Item name="recipientIds" hidden>
              <AntInput />
            </Form.Item>
          </Form>
        </div>
      </section>

      <FixedBottomActions
        leftActions={
          <Space>
            <Button
              variant="primary"
              onClick={() => form.submit()}
              loading={inertiaForm.processing}
            >
              {formMode === 'edit' ? 'Save Changes' : 'Create Push Notification'}
            </Button>
            <Button variant="secondary" icon={<EyeOutlined />} onClick={handlePreview}>
              Preview
            </Button>
          </Space>
        }
        rightActions={
          <Button variant="tertiary" onClick={handleCancel}>
            Cancel
          </Button>
        }
      />

      <RecipientSelectionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onApply={handleRecipientApply}
        recipients={availableRecipients}
        selectedRecipientIds={selectedRecipientIds}
        initialFilters={recipientFilters}
        filterOptions={filterOptions}
      />

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={titleValue}
        content={contentValue}
      />
    </EventsLayout>
  )
}

export default PushNotificationFormPage
